import { Card, CardContent } from "@/components/ui/card"
import { useCurrency } from "@/context/currency/currency-context"
import { InfoTip } from "@/components/custom/fire/info-tip"
import type { FireSettings } from "@/types/fire"

interface FireHeroProps {
	annualDividendIncome: number
	settings: FireSettings
	freedomYear: number | null
	portfolioValue: number
}

function getCoverageGradient(pct: number): string {
	if (pct >= 100) return "from-green-500 to-emerald-400"
	if (pct >= 75) return "from-lime-500 to-green-400"
	if (pct >= 50) return "from-amber-500 to-yellow-400"
	if (pct >= 25) return "from-orange-500 to-amber-400"
	return "from-rose-500 to-orange-400"
}

function getYearsGradient(years: number | null): string {
	if (years === null) return "from-sky-500 to-blue-400"
	if (years === 0) return "from-green-500 to-emerald-400"
	if (years <= 5) return "from-lime-500 to-green-400"
	if (years <= 15) return "from-amber-500 to-yellow-400"
	return "from-sky-500 to-blue-400"
}

function GradientNumber({ value, gradient }: { value: string; gradient: string }) {
	return <p className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>{value}</p>
}

export function FireHero({ annualDividendIncome, settings, freedomYear, portfolioValue }: FireHeroProps) {
	const { format, convert } = useCurrency()

	const annualGoal = convert(settings.monthly_expense_target * 12, settings.currency)
	const coveragePct = annualGoal > 0 ? Math.min(Math.round((annualDividendIncome / annualGoal) * 100), 999) : 0

	const currentYear = new Date().getFullYear()
	const yearsToFreedom = freedomYear ? (freedomYear <= currentYear ? 0 : freedomYear - currentYear) : null

	const coverageGradient = getCoverageGradient(coveragePct)
	const yearsGradient = getYearsGradient(yearsToFreedom)

	return (
		<div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30 last:col-span-2 lg:last:col-span-1">
				<CardContent className="pt-6">
					<p className="text-sm text-muted-foreground flex items-center gap-1">
						Income Coverage
						<InfoTip content="Percentage of your annual expense goal currently covered by trailing 12-month dividend income." />
					</p>
					{annualGoal > 0 ? (
						<GradientNumber value={`${coveragePct}%`} gradient={coverageGradient} />
					) : (
						<p className="text-3xl font-bold text-muted-foreground">—</p>
					)}
					<p className="text-xs text-muted-foreground mt-1">of annual goal covered</p>
				</CardContent>
			</Card>

			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30 last:col-span-2 lg:last:col-span-1">
				<CardContent className="pt-6">
					<p className="text-sm text-muted-foreground flex items-center gap-1">
						Years to Freedom
						<InfoTip content="Estimated years until projected dividend income covers your monthly expense target, based on your growth, contribution, and DRIP settings." />
					</p>
					{yearsToFreedom === null ? (
						<p className="text-3xl font-bold text-muted-foreground">—</p>
					) : (
						<GradientNumber
							value={yearsToFreedom === 0 ? "Free! 🎉" : `${yearsToFreedom}y`}
							gradient={yearsGradient}
						/>
					)}
					<p className="text-xs text-muted-foreground mt-1">
						{freedomYear
							? `Est. ${freedomYear}`
							: annualGoal > 0
								? "Beyond 50-year projection"
								: "Set an income goal"}
					</p>
				</CardContent>
			</Card>

			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30 last:col-span-2 lg:last:col-span-1">
				<CardContent className="pt-6">
					<p className="text-sm text-muted-foreground">Monthly Income</p>
					<GradientNumber
						value={format(annualDividendIncome / 12, { skipConversion: true, noDecimals: true })}
						gradient="from-sky-500 to-blue-400"
					/>
					<p className="text-xs text-muted-foreground mt-1">trailing 12-month avg</p>
				</CardContent>
			</Card>

			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30 last:col-span-2 lg:last:col-span-1">
				<CardContent className="pt-6">
					<p className="text-sm text-muted-foreground">Yearly Income</p>
					<GradientNumber
						value={format(annualDividendIncome, { skipConversion: true, noDecimals: true })}
						gradient="from-violet-500 to-purple-400"
					/>
					<p className="text-xs text-muted-foreground mt-1">trailing 12 months</p>
				</CardContent>
			</Card>

			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30 last:col-span-2 lg:last:col-span-1">
				<CardContent className="pt-6">
					<p className="text-sm text-muted-foreground flex items-center gap-1">
						Capital Start
						<InfoTip content="Total portfolio value including stock positions and cash balances, in your display currency." />
					</p>
					<GradientNumber
						value={format(portfolioValue, { skipConversion: true, noDecimals: true })}
						gradient="from-teal-500 to-cyan-400"
					/>
					<p className="text-xs text-muted-foreground mt-1">current portfolio value</p>
				</CardContent>
			</Card>
		</div>
	)
}
